<template>
    <div class="segmented-toggle" :class="size && size !== 'normal' ? `is-${size}` : ''">
        <div class="segmented-toggle__thumb" :style="thumbStyle"></div>
        <button
            v-for="(option, index) in options"
            :key="String(option.value)"
            ref="optionEls"
            type="button"
            class="segmented-toggle__option"
            :class="{ 'is-active': index === visualActiveIndex }"
            :disabled="isLoading"
            @click="onSelect(option, index)"
        >
            <span class="icon segmented-toggle__indicator" v-if="index === visualActiveIndex">
                <span v-if="isLoading" class="segmented-toggle__spinner"></span>
                <i v-else class="fas fa-check"></i>
            </span>
            <span class="icon" v-else-if="option.icon">
                <i :class="option.icon"></i>
            </span>
            <span>{{ option.label }}</span>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    export interface SegmentedToggleOption {
        value: string | number | boolean;
        label: string;
        icon?: string;
    }

    const props = defineProps<{
        options: SegmentedToggleOption[];
        modelValue: string | number | boolean;
        isLoading?: boolean;
        // Mirrors Bulma's button size modifiers, so the control fits inline next to other buttons/inputs.
        size?: "small" | "normal" | "medium" | "large";
    }>();

    const emit = defineEmits<{
        change: [value: string | number | boolean];
    }>();

    const activeIndex = computed(() => props.options.findIndex(option => option.value === props.modelValue));

    // The consumer owns modelValue and only updates it once its async work resolves (see
    // onSelect below), so without this the pill/spinner would sit on the still-current segment
    // while loading, giving no feedback where the user just clicked. Tracking the clicked index
    // separately lets the pill move there immediately; it's cleared once isLoading ends, at which
    // point activeIndex has either caught up (success) or the pill reverts on its own (failure).
    const pendingIndex = ref<number | null>(null);
    const visualActiveIndex = computed(() => pendingIndex.value ?? activeIndex.value);

    const optionEls = ref<HTMLButtonElement[]>([]);
    const thumbLeft = ref(0);
    const thumbWidth = ref(0);

    // Options can have differently-sized labels (e.g. "Live" vs "Planned"), so the pill can't
    // assume equal-width segments - it measures the active button's real box instead.
    const thumbStyle = computed(() => ({
        left: `${thumbLeft.value}px`,
        width: `${thumbWidth.value}px`,
    }));

    function updateThumb() {
        const activeEl = optionEls.value[visualActiveIndex.value];

        if (!activeEl) {
            return;
        }

        thumbLeft.value = activeEl.offsetLeft;
        thumbWidth.value = activeEl.offsetWidth;
    }

    onMounted(() => {
        nextTick(updateThumb);
        window.addEventListener("resize", updateThumb);
    });

    onBeforeUnmount(() => {
        window.removeEventListener("resize", updateThumb);
    });

    watch(
        () => [visualActiveIndex.value, props.options],
        () => nextTick(updateThumb),
    );

    watch(
        () => props.isLoading,
        loading => {
            if (!loading) {
                pendingIndex.value = null;
            }
        },
    );

    function onSelect(option: SegmentedToggleOption, index: number) {
        if (props.isLoading || option.value === props.modelValue) {
            return;
        }

        pendingIndex.value = index;
        emit("change", option.value);
    }
</script>

<style scoped>
    .segmented-toggle {
        position: relative;
        display: inline-flex;
        box-sizing: border-box;
        height: var(--bulma-control-height);
        padding: 3px;
        background-color: var(--bulma-scheme-main);
        border: 1px solid var(--bulma-border);
        border-radius: var(--bulma-control-radius);
        font-size: var(--bulma-size-normal);
    }

    .segmented-toggle.is-small {
        font-size: var(--bulma-size-small);
        border-radius: var(--bulma-radius-small);
    }

    .segmented-toggle.is-medium {
        font-size: var(--bulma-size-medium);
        border-radius: var(--bulma-radius-medium);
    }

    .segmented-toggle.is-large {
        font-size: var(--bulma-size-large);
        border-radius: var(--bulma-radius-medium);
    }

    .segmented-toggle__thumb {
        position: absolute;
        top: 3px;
        bottom: 3px;
        background-color: var(--bulma-primary);
        border-radius: inherit;
        transition:
            left 0.2s ease,
            width 0.2s ease;
    }

    .segmented-toggle__option {
        position: relative;
        z-index: 1;
        display: flex;
        height: 100%;
        align-items: center;
        justify-content: center;
        gap: 0.4em;
        padding: 0 1em;
        font-size: inherit;
        line-height: 1;
        background: none;
        border: none;
        border-radius: inherit;
        color: var(--bulma-text-strong);
        cursor: pointer;
        transition: color 0.2s ease;
        white-space: nowrap;
    }

    .segmented-toggle__option.is-active {
        color: var(--bulma-primary-invert);
    }

    .segmented-toggle__option:disabled {
        cursor: not-allowed;
        opacity: 0.85;
    }

    .segmented-toggle__indicator {
        color: inherit;
    }

    /* The fa-check glyph's ink sits higher than its em-box center, reading as vertically
       misaligned against the label text next to it even though both boxes are centered
       identically - nudge it down slightly to correct the optical (not geometric) offset. */
    .segmented-toggle__indicator .fa-check {
        position: relative;
        top: 1px;
    }

    /* Matches Bulma's own .button.is-loading::after spinner (same ring/animation) instead of
       InlineSpinner's FontAwesome icon, so loading state looks identical to every other button
       in the app. */
    .segmented-toggle__spinner {
        display: block;
        width: 1em;
        height: 1em;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-top-color: transparent;
        border-radius: var(--bulma-radius-rounded);
        animation: segmented-toggle-spin 500ms infinite linear;
    }

    @keyframes segmented-toggle-spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(359deg);
        }
    }
</style>
